import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { InvoiceDocumentType } from '../../enums/invoiceDocumentType'
import { SendAsEmailModalVariant } from '../../enums/sendAsEmailModalVariant'
import { SendAsEmailForm } from '../../types/sendAsEmailForm'
import { getSendAsEmailCaptchaToken } from '../../utils/sendInvoiceAsEmail/getSendAsEmailCaptchaToken'
import { getSendInvoiceAsEmailPayload } from '../../utils/sendInvoiceAsEmail/getSendAsEmailPayload'
import { notifyEmailSendError } from '../../utils/sendInvoiceAsEmail/notifyEmailSendError'
import { notifyEmailSendSuccess } from '../../utils/sendInvoiceAsEmail/notifyEmailSendSuccess'
import { useInvoicingApiActionSegmentEvents } from '../useInvoicingApiActionSegmentEvents'
import { useSendAsEmailMutation } from './useSendAsEmailMutation'

interface UseSendInvoiceAsEmailProps {
  onError?: (invoiceId: number) => void
  onSuccess?: (invoiceId: number) => void
}

interface SendAsEmailOptions {
  acceptStripePayment?: boolean
  documentType: InvoiceDocumentType
  formData: SendAsEmailForm
  invoiceId: number
}

export const useSendAsEmail = (variant: SendAsEmailModalVariant, props?: UseSendInvoiceAsEmailProps) => {
  const { onError, onSuccess } = props || {}
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { send, isProcessing } = useSendAsEmailMutation({ variant })
  const { sendEvent } = useInvoicingApiActionSegmentEvents()

  const organizationEmail = organization?.email

  const handleError = useCallback(
    (invoiceId: number) => {
      notifyEmailSendError(t)
      onError?.(invoiceId)
    },
    [onError, t],
  )

  const sendAsEmail = useCallback(
    async ({ acceptStripePayment, documentType, formData, invoiceId }: SendAsEmailOptions) => {
      const captchaToken = await getSendAsEmailCaptchaToken()
      if (!captchaToken) {
        handleError(invoiceId)
        return
      }

      const sendAsEmailPayload = getSendInvoiceAsEmailPayload({
        acceptStripePayment,
        organizationEmail,
        captchaToken,
        formData,
      })

      send(
        { documentType, invoiceId, payload: sendAsEmailPayload },
        {
          onSuccess: () => {
            if (variant === SendAsEmailModalVariant.Invoice) {
              sendEvent.document.sent(documentType, { isStripePaymentEnabled: acceptStripePayment })
            }

            notifyEmailSendSuccess(t)
            onSuccess?.(invoiceId)
          },
          onError: () => {
            handleError(invoiceId)
          },
        },
      )
    },
    [handleError, onSuccess, organizationEmail, send, sendEvent.document, t, variant],
  )

  return { sendAsEmail, isProcessing }
}
